import { useSyncExternalStore } from 'react';
import { en } from './en';
import { pt } from './pt';

/** The message shape every locale must provide (English is the source of truth). */
export type Messages = typeof en;

export type Locale = 'en' | 'pt';

/** All supported locales, in display order (used by the language switcher). */
export const locales: Locale[] = ['pt', 'en'];

const dictionaries: Record<Locale, Messages> = { en, pt };

/** localStorage key holding an explicit language choice (absent ⇒ follow browser). */
const KEY = 'cr7site:lang';

const isLocale = (v: string | null): v is Locale => v === 'en' || v === 'pt';

/**
 * Browser default: Portuguese for any `pt*` locale (pt, pt-PT, pt-BR…),
 * English for everything else.
 */
const detectLocale = (): Locale =>
  (navigator.language || 'pt').toLowerCase().startsWith('pt') ? 'pt' : 'en';

const readInitial = (): Locale => {
  const stored = window.localStorage.getItem(KEY);
  return isLocale(stored) ? stored : detectLocale();
};

// Single source of truth. Held at module level so non-React callers can read the
// active language at call time, while React components subscribe via `useI18n`
// and re-render when it changes.
let current: Locale = readInitial();
const listeners = new Set<() => void>();

const subscribe = (cb: () => void): (() => void) => {
  listeners.add(cb);
  return () => {
    listeners.delete(cb);
  };
};

/** The active locale right now. */
export const getLocale = (): Locale => current;

/** The active dictionary right now — read at call time so it reflects switches. */
export const getT = (): Messages => dictionaries[current];

/** Switch language: persist the choice, update <html lang>, and re-render the UI. */
export const setLocale = (next: Locale): void => {
  if (next === current) return;
  current = next;
  window.localStorage.setItem(KEY, next);
  document.documentElement.lang = next;
  listeners.forEach((cb) => cb());
};

/** React hook: the active dictionary plus the current locale and a setter. */
export const useI18n = (): { t: Messages; locale: Locale; setLocale: typeof setLocale } => {
  const locale = useSyncExternalStore(subscribe, getLocale, getLocale);
  return { t: dictionaries[locale], locale, setLocale };
};
